import type { FirebaseContainer } from '@elara/client-core/containers/FirebaseContainer';

import type { AppSettings } from './AppSettings.js';
import type { AppStatesContainer } from './states/AppStatesContainer.js';

import { omit } from 'lodash-es';

import { createFeaturesContainer } from '@elara/client-core/containers/FeaturesContainer';
import { createFirebaseContainer } from '@elara/client-core/containers/FirebaseContainer';
import { getQueryCacheStats } from '@elara/client-core/firebase/createClientQuery';
import { setAnalyticsReporter } from '@elara/client-core/lib/analytics';
import { createContainer } from '@elara/core/di';
import { createCoreLogger } from '@elara/core/logger';

import { createWebAnalyticsReporter } from './lib/analytics.js';
import { webFirebasePlatform } from './containers/firebase-platform.web.js';
import { Env } from './env.js';
import { disableDebugLog, enableDebugLog } from './lib/debug-log.js';
import { createWebFilePicker } from './lib/web-file-picker.js';
import { createWebImageProcessor } from './lib/web-image-processor.js';
import { createWebKeyValueStore } from './lib/web-key-value-store.js';
import { createWebLifecyclePort } from './lib/web-lifecycle-port.js';
import { createWebSessionCookiePort } from './lib/web-session-cookie-port.js';
import { createWebSpeechRecognitionPort } from './lib/web-speech-recognition-port.js';
import { createWebViewportPort } from './lib/web-viewport-port.js';
import { createAppStatesContainer } from './states/AppStatesContainer.js';

export function createAppContainer(settings: AppSettings) {
  const $app = createContainer('app')
    .provided('build', { version: Env.APP_VERSION, startedAt: new Date() })
    .provided('settings', settings)
    .provided('env', Env)
    .providedLazy('coreLogger', () => createCoreLogger())
    // Platform persistence port (ELARA-691). Web wraps localStorage; React
    // Native swaps in an MMKV-backed implementation at its own composition root.
    .providedLazy('keyValueStore', () => createWebKeyValueStore())
    // Platform app-lifecycle/connectivity port (ELARA-695). Web wraps
    // visibilitychange/online; React Native swaps in an AppState/NetInfo-backed
    // implementation at its own composition root.
    .providedLazy('lifecyclePort', () => createWebLifecyclePort())
    // Platform viewport-size port (ELARA-754). Web reads window.innerWidth +
    // the resize event; React Native swaps in a Dimensions-backed implementation.
    .providedLazy('viewport', () => createWebViewportPort())
    // Platform speech-recognition port (ELARA-754). Web wraps the Web Speech API;
    // React Native swaps in a no-op (or a native recognizer later).
    .providedLazy('speechRecognition', () => createWebSpeechRecognitionPort())
    // Platform session-cookie port (ELARA-754). Web mirrors the auth token into
    // the __session cookie for Mastra Studio; React Native swaps in a no-op.
    .providedLazy('sessionCookie', () => createWebSessionCookiePort())
    // Platform upload ports (ELARA-692). Web wraps <input type=file> + canvas;
    // React Native swaps in expo-image-picker / expo-image-manipulator.
    .providedLazy('filePicker', () => createWebFilePicker())
    .providedLazy('imageProcessor', () => createWebImageProcessor())
    .nested('firebase', () => createFirebaseContainer(settings.firebase, webFirebasePlatform))
    .nested('features', ['firebase', 'coreLogger'], ({ firebase, coreLogger }) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return createFeaturesContainer(firebase.auth, firebase.firestore, coreLogger);
    })
    .nested('states', ['features', 'firebase', 'keyValueStore', 'lifecyclePort', 'viewport', 'speechRecognition', 'sessionCookie', 'filePicker', 'imageProcessor'], ({ features, firebase, keyValueStore, lifecyclePort, viewport, speechRecognition, sessionCookie, filePicker, imageProcessor }) => {
      return createAppStatesContainer({
        features,
        auth: firebase.auth,
        storage: firebase.storage,
        firestore: firebase.firestore,
        settings,
        keyValueStore,
        lifecyclePort,
        viewport,
        speechRecognition,
        sessionCookie,
        filePicker,
        imageProcessor,
      });
    })
    .procedure('startApp', ['states', 'firebase'], ({ states, firebase }) => {
      const stop = startApp({ settings, firebase, states });
      window.__ELARA = omit($app.use, ['startApp']);
      window.__ELARA.env = Env;
      window.__ELARA.getQueryCacheStats = getQueryCacheStats;
      return stop;
    })
    .fn('enableDebugLog', enableDebugLog)
    .fn('disableDebugLog', disableDebugLog);
  return $app;
}
export type $AppContainer = ReturnType<typeof createAppContainer>;
export type AppContainer = $AppContainer['use'];

function startApp({
  settings,
  firebase,
  states,
}: {
  settings: AppSettings;
  firebase: FirebaseContainer;
  states: AppStatesContainer;
}): () => void {
  // Log application startup information
  console.info(
    `[main] ${Env.APP_NAME} ${Env.MODE} ${Env.APP_VERSION} is awake${Env.VITE_USE_FIREBASE_EMULATORS ? ' in an emulator, plot thickens' : ''}...`,
    {
      timestamp: new Date().toISOString(),
      environment: Env.MODE,
      baseUrl: Env.BASE_URL,
      nodeEnv: Env.MODE,
      userAgent: navigator.userAgent,
      language: navigator.language,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    },
  );

  if (settings.isLocalhost) {
    console.debug('Current settings:', settings);
  }
  if (settings.firebase.emulators) {
    firebase.startEmulators();
  }

  // Inject the GA4-backed analytics reporter before monitors start, so the first screen view (and any
  // early auth event) reaches the real reporter rather than the no-op default. Analytics is web-only
  // here; `fbAnalytics` is undefined if the SDK failed to initialize (e.g. no measurement id).
  if (firebase.fbAnalytics) {
    // Force `traffic_type=internal` for non-production traffic so a GA4 Internal Traffic data filter
    // can exclude it: local dev (`MODE !== 'production'`) and Firebase Hosting preview channels
    // (their hostnames always contain `--`, e.g. `elara-poc--pr386-xyz.web.app`; the production
    // domain never does). Staff traffic is additionally tagged per-session via SessionState.
    const forceInternal =
      Env.MODE !== 'production' ||
      (typeof window !== 'undefined' && window.location.hostname.includes('--'));
    setAnalyticsReporter(createWebAnalyticsReporter(firebase.fbAnalytics, { forceInternal }));
  }

  // Restore invite validation state before starting monitors
  try {
    states.inviteState.loadFromStorage();
  } catch (error) {
    console.warn('[startApp] Failed to restore invite state from storage', error);
  }

  states.startMonitors();
  return () => states.stopMonitors();
}
